import { bound } from "@frui.ts/helpers";

type Action = () => Promise<void>;

class RequestRecoveryService {
  static MAX_TRIES = 5;
  static TIMEOUT_BETWEEN_STEPS = 2000;
  private runningTimeouts = new Map<string, NodeJS.Timeout>();
  private plannedActions = new Map<string, Action>();

  constructor() {
    window.addEventListener("online", this.onBecomeOnline);
  }

  @bound
  private onBecomeOnline() {
    this.plannedActions.forEach((action, name) => {
      action()
        .then(() => this.cancelTask(name))
        .catch(() => this.addTask(name, action));
    });
  }

  private performTask(name: string, action: Action, failedCounter: number) {
    if (!navigator.onLine) {
      this.runningTimeouts.delete(name);
      this.plannedActions.set(name, action);
      return;
    } else if (navigator.onLine && failedCounter < RequestRecoveryService.MAX_TRIES) {
      this.plannedActions.delete(name);
      this.runningTimeouts.set(
        name,
        setTimeout(
          () =>
            action()
              .then(() => this.cancelTask(name))
              .catch(() => {
                this.performTask(name, action, failedCounter + 1);
              }),
          (failedCounter + 1) * RequestRecoveryService.TIMEOUT_BETWEEN_STEPS
        )
      );
      return;
    } else {
      this.cancelTask(name);
      return;
    }
  }

  addTask(name: string, action: Action) {
    this.performTask(name, action, 0);
  }

  cancelTask(name: string) {
    this.plannedActions.delete(name);
    if (this.runningTimeouts.has(name)) {
      clearTimeout(this.runningTimeouts.get(name) as NodeJS.Timeout);
      this.runningTimeouts.delete(name);
    }
  }
}

const requestRecoveryService = new RequestRecoveryService();
export default requestRecoveryService;

/**
 * It can be used as a decorator of an asynchronous method which it will try to call again if it fails
 */
export function repeatIfFailed(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
  const originalFunction = descriptor.value;

  descriptor.value = function (this: any, ...args: any[]) {
    const result = originalFunction.apply(this, args);

    if (result) {
      result
        .then(() => {
          requestRecoveryService.cancelTask(propertyKey);
        })
        .catch((error: any) => {
          requestRecoveryService.addTask(propertyKey, () => originalFunction.apply(this));
        });
    }

    return result;
  };
}
